import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getMyPaymentSubscription } from '../services/mockInterviewService';
import { hasRole } from '../utils/auth';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    // Defensive default so non-wrapped trees don't crash during dev.
    return {
      subscription: null,
      loading: false,
      refresh: () => Promise.resolve(null),
      clear: () => {},
      isPaid: false,
    };
  }
  return ctx;
};

// Only candidates pay — admins/interviewers don't trigger fetches.
const isCandidate = () =>
  !!localStorage.getItem('token') &&
  hasRole('User') &&
  !hasRole('Admin') &&
  !hasRole('Interviewer');

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isCandidate()) {
      setSubscription(null);
      return null;
    }
    setLoading(true);
    try {
      const data = await getMyPaymentSubscription();
      setSubscription(data || null);
      return data;
    } catch {
      // Endpoint always returns a response per spec; if we land here it's a
      // network/auth error — keep prior state and let callers retry.
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => setSubscription(null), []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      subscription,
      loading,
      refresh,
      clear,
      isPaid: !!subscription && subscription.planKey && subscription.planKey !== 'Free',
    }),
    [subscription, loading, refresh, clear],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
